"use client";
import React, { useEffect, useRef, useState } from "react";

type VideoSource = { src: string; type?: string };

type Props = {
  src?: string; // fallback single source
  sources?: VideoSource[]; // prefer list of sources (e.g., webm then mp4)
  poster?: string;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  preload?: "auto" | "metadata" | "none";
  ariaLabel?: string;
  controls?: boolean;
};

export default function DeferredVideo({
  src,
  sources,
  poster,
  className,
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  preload = "none",
  ariaLabel,
  controls = false,
}: Props) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    let observer: IntersectionObserver | null = null;

    const el = ref.current;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer?.disconnect();
            observer = null;
          }
        },
        { threshold: 0.1 }, // reduced threshold for better UX
      );
      observer.observe(el);
    } else {
      // Fallback: load immediately
      setShouldLoad(true);
    }

    return () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };
  }, [ref]);

  useEffect(() => {
    if (!shouldLoad || !ref.current) return;
    // assign sources only when we want to start loading
    if (!ref.current.querySelector("source")) {
      const toAdd =
        sources && sources.length
          ? sources
          : src
            ? [{ src, type: undefined }]
            : [];

      toAdd.forEach((s) => {
        const source = document.createElement("source");
        source.src = s.src;
        source.type =
          s.type || (s.src.endsWith(".webm") ? "video/webm" : "video/mp4");
        ref.current?.appendChild(source);
      });

      // try to load
      try {
        ref.current.load();
        // Only autoplay if it's meant to be a background video (no controls)
        if (autoPlay && !controls) {
          // some browsers prevent autoplay without user gesture; ignore errors
          ref.current.play().catch(() => {});
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [shouldLoad, src, sources, autoPlay, controls]);

  return (
    <video
      ref={ref}
      className={className}
      playsInline={playsInline}
      muted={muted}
      loop={loop}
      preload={preload}
      poster={poster}
      aria-label={ariaLabel}
      controls={controls}
      // don't set src on the element to prevent preload before intersection
    >
      {/* Decorative background videos have no dialogue; the captions track
          satisfies the Lighthouse/WCAG audit for <video> elements. */}
      <track
        kind="captions"
        srcLang="en"
        label="English"
        src={`${process.env.NEXT_PUBLIC_BASE_PATH || ""}/captions/decorative.vtt`}
      />
    </video>
  );
}
